import { useQueryClient } from '@tanstack/react-query';

import { useSecretMutation } from '@/hooks/use-secret-mutation';

import { enrollmentService } from '@/services/enrollment-service';
import type { RotateEnrollmentSecretResult } from '@/types/core/enrollment';
import type { UUID } from '@/utils/uuid';

import { enrollmentMutationKeys, enrollmentQueryKeys } from './query-keys';

/**
 * Rotate a shared enrollment secret. The old value stops working immediately;
 * the new value is returned once.
 */
export function useRotateEnrollmentSecret(tenantId?: UUID) {
  const qc = useQueryClient();

  return useSecretMutation<RotateEnrollmentSecretResult, UUID>({
    mutationKey: enrollmentMutationKeys.rotateSecret(tenantId),
    mutationFn: async (credentialId) => {
      if (!tenantId) throw new Error('Missing tenant id');
      return enrollmentService.rotateSecret(tenantId, credentialId);
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: enrollmentQueryKeys.credentialsByTenant(tenantId),
        refetchType: 'all',
      });
    },
  });
}
